import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { Toaster } from "@/components/ui/sonner";

const L = (load, name = "default") => lazy(() => load().then((m) => ({ default: m[name] })).catch((e) => {
  const last = Number(sessionStorage.getItem("sq_chunk_reload") || 0);
  if (Date.now() - last > 15000) { sessionStorage.setItem("sq_chunk_reload", String(Date.now())); window.location.reload(); }
  throw e;
}));
const Landing = L(() => import("@/pages/Landing"));
const DemoManage = L(() => import("@/pages/DemoManage"));
const DemoShort = L(() => import("@/pages/DemoShort"));
const Login = L(() => import("@/pages/Login"));
const Register = L(() => import("@/pages/Register"));
const ForgotPassword = L(() => import("@/pages/ForgotPassword"));
const ResetPassword = L(() => import("@/pages/ResetPassword"));
const Onboarding = L(() => import("@/pages/Onboarding"));
const AppLayout = L(() => import("@/components/AppLayout"));
const Dashboard = L(() => import("@/pages/Dashboard"));
const Jobs = L(() => import("@/pages/Jobs"));
const JobDetail = L(() => import("@/pages/JobDetail"));
const ScanWizard = L(() => import("@/pages/ScanWizard"));
const VisionLanding = L(() => import("@/pages/vision/VisionLanding"));
const VisionDemoRender = L(() => import("@/pages/vision/VisionDemoRender"));
const VisionProjects = L(() => import("@/pages/vision/VisionProjects"));
const VisionStudio = L(() => import("@/pages/vision/VisionStudio"));
const VisionShare = L(() => import("@/pages/vision/VisionShare"));
const Quotes = L(() => import("@/pages/Quotes"));
const WorkOrders = L(() => import("@/pages/WorkOrders"));
const QuoteEditor = L(() => import("@/pages/QuoteEditor"));
const Customers = L(() => import("@/pages/Customers"));
const CustomerDetail = L(() => import("@/pages/CustomerDetail"));
const PriceList = L(() => import("@/pages/PriceList"));
const Settings = L(() => import("@/pages/Settings"));
const AppointmentPublic = L(() => import("@/pages/AppointmentPublic"));
const Sopralluoghi = L(() => import("@/pages/Sopralluoghi"));
const Credits = L(() => import("@/pages/Credits"));
const Packs = L(() => import("@/pages/Packs"));
const PacksPublic = L(() => import("@/pages/Packs"), "PacksPublic");
const Pricing = L(() => import("@/pages/Pricing"));
const Analytics = L(() => import("@/pages/Analytics"));
const More = L(() => import("@/pages/More"));
const Notifications = L(() => import("@/pages/Notifications"));
const SnapBid = L(() => import("@/pages/snapbid/SnapBid"));
const AuctionDetail = L(() => import("@/pages/snapbid/AuctionDetail"));
const SnapBidProfile = L(() => import("@/pages/snapbid/SnapBidProfile"));
const Purchased = L(() => import("@/pages/snapbid/Purchased"));
const SnapBidLanding = L(() => import("@/pages/SnapBidLanding"));
const ClientUpload = L(() => import("@/pages/ClientUpload"));
const SnapBidVideoExport = L(() => import("@/pages/SnapBidVideoExport"));
const ExplainerExport = L(() => import("@/pages/ExplainerExport"));
const PublicQuote = L(() => import("@/pages/PublicQuote"));
const PublicContract = L(() => import("@/pages/PublicContract"));
const Protection = L(() => import("@/pages/Protection"));
const ProtectedDeposits = L(() => import("@/pages/ProtectedDeposits"));
const SaldoProtetto = L(() => import("@/pages/SaldoProtetto"));
const VerifyEmail = L(() => import("@/pages/VerifyEmail"));
const WorkOrderDetail = L(() => import("@/pages/WorkOrderDetail"));
const PaymentResult = L(() => import("@/pages/PaymentResult"));
const Privacy = L(() => import("@/pages/Legal"), "Privacy");
const CookiePolicy = L(() => import("@/pages/Legal"), "CookiePolicy");
const Terms = L(() => import("@/pages/Legal"), "Terms");
const Contatti = L(() => import("@/pages/Legal"), "Contatti");
const TerminiPacchetti = L(() => import("@/pages/TerminiPacchetti"));
import { CookieBanner, MetaPixel } from "@/components/CookieBanner";
import { RouteMeta } from "@/components/RouteMeta";
import { WhatsAppButton } from "@/components/common/WhatsAppButton";
import { RefCapture } from "@/components/common/ReferralCapture";
import { captureAttribution } from "@/lib/analytics";
import { initTradedoubler } from "@/lib/tradedoubler";
import { IntentRedirect, takeIntent } from "@/lib/intent";
import { AdsConversion } from "@/lib/adsConversion";
const MasterAdmin = L(() => import("@/pages/master/MasterAdmin"));
const PrivatiHome = L(() => import("@/pages/privati/PrivatiHome"));
const CasaLanding = L(() => import("@/pages/casa/CasaLanding"));
const CasaStudio = L(() => import("@/pages/casa/CasaStudio"));
const CasaAuth = L(() => import("@/pages/casa/CasaAuth"));
const CasaReset = L(() => import("@/pages/casa/CasaReset"));
const ImpresaContatto = L(() => import("@/pages/casa/ImpresaContatto"));
const CasaPagamentiPage = L(() => import("@/pages/casa/CasaPages"), "CasaPagamentiPage");
const CasaSopralluogoPage = L(() => import("@/pages/casa/CasaPages"), "CasaSopralluogoPage");
const RequestWizard = L(() => import("@/pages/privati/RequestWizard"));
const InfoPage = L(() => import("@/pages/privati/InfoPages"));
const cliente = () => import("@/pages/cliente/ClienteArea");
const CustomerCallback = L(cliente, "CustomerCallback");
const CustomerAccess = L(cliente, "CustomerAccess");
const CustomerDashboard = L(cliente, "CustomerDashboard");
const CustomerJob = L(cliente, "CustomerJob");
const CustomerDocuments = L(cliente, "CustomerDocuments");
const CustomerSupport = L(cliente, "CustomerSupport");
const VerifiedPublic = L(() => import("@/pages/VerifiedPublic"));
const VerifiedApply = L(() => import("@/pages/VerifiedApply"));
const StoryExport = L(() => import("@/pages/StoryExport"));
const PublicSite = L(() => import("@/pages/PublicSite"));
const Directory = L(() => import("@/pages/Directory"));
const ProProfile = L(() => import("@/pages/ProProfile"));
const Website = L(() => import("@/pages/Website"));
const PublicProfileEdit = L(() => import("@/pages/PublicProfileEdit"));
const Leads = L(() => import("@/pages/Leads"));
const Platinum = L(() => import("@/pages/Platinum"));
const SofiaLanding = L(() => import("@/pages/SofiaLanding"));
const FounderLanding = L(() => import("@/pages/FounderLanding"));
const FounderThanks = L(() => import("@/pages/FounderThanks"));
const FounderConfirmed = L(() => import("@/pages/FounderConfirmed"));
const BankStart = L(() => import("@/pages/BankStart"));
const ApplicationConfirmed = L(() => import("@/pages/ApplicationConfirmed"));
const OppAlertsConsent = L(() => import("@/pages/OppAlertsConsent"));
const FounderMeta = L(() => import("@/pages/FounderMeta"));
const AcademyLanding = L(() => import("@/pages/AcademyLanding"));
const FounderArea = L(() => import("@/pages/FounderArea"));
const PlatinumLanding = L(() => import("@/pages/PlatinumLanding"));
const RossiSite = L(() => import("@/pages/demo/RossiSite"));
const Sofia = L(() => import("@/pages/Sofia"));
const FreeEstimate = L(() => import("@/pages/FreeEstimate"));
const ClaimDraft = L(() => import("@/pages/ClaimDraft"));
const RefRedirect = L(() => import("@/pages/RefRedirect"));
const PaymentReceived = L(() => import("@/pages/PaymentReceived"));
const ConfirmedInspections = L(() => import("@/pages/ConfirmedInspections"));
const BankOrder = L(() => import("@/pages/BankOrder"));
const ActivateAccount = L(() => import("@/pages/ActivateAccount"));

captureAttribution();
initTradedoubler();

function Splash() {
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-5 bg-[#0B0D10] px-6 text-center">
      <div className="w-10 h-10 rounded-full border-2 border-[#3BE477] border-t-transparent sq-spin" />
      {slow && (
        <div className="max-w-xs" data-testid="splash-retry">
          <p className="text-sm text-[#A7B0BA] mb-3">Sta impiegando più del previsto. Controlla la connessione e riprova.</p>
          <button onClick={() => window.location.reload()} className="h-10 px-5 rounded-xl bg-[#3BE477] text-[#0B0D10] text-sm font-semibold active:scale-95 transition">Riprova</button>
        </div>
      )}
    </div>
  );
}

function Protected({ children }) {
  const { user, settings } = useAuth();
  const location = useLocation();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/login" replace />;
  const draftPath = /^\/app\/(salva-bozza|preventivi\/)/.test(location.pathname);
  if (settings && settings.onboarding_done === false && location.pathname !== "/onboarding" && !(draftPath && (user.growth || location.pathname === "/app/salva-bozza"))) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to="/app" replace />;
  return children;
}

function AuthCallback() {
  const navigate = useNavigate();
  const { refreshMe } = useAuth();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const params = new URLSearchParams(window.location.hash.replace("#", ""));
    const sid = params.get("session_id");
    (async () => {
      try {
        await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        window.history.replaceState(null, "", window.location.pathname);
        await refreshMe();
        navigate(takeIntent() || "/app", { replace: true });
      } catch {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, refreshMe]);
  return <Splash />;
}

function AppRoutes() {
  const location = useLocation();
  const fallback = location.pathname.startsWith("/casa") ? <div className="min-h-screen bg-white" /> : <Splash />;
  return <Suspense fallback={fallback}><PageRoutes location={location} /></Suspense>;
}

function PageRoutes({ location }) {
  if (location.hash && location.hash.includes("session_id=")) {
    if (location.pathname.startsWith("/cliente") || location.pathname.startsWith("/casa")) return <CustomerCallback />;
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
      <Route path="/register" element={<PublicOnly><Register /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/cookie" element={<CookiePolicy />} />
      <Route path="/termini" element={<Terms />} />
      <Route path="/termini-pacchetti-sopralluoghi" element={<TerminiPacchetti />} />
      <Route path="/contatti" element={<Contatti />} />
      <Route path="/snapbid" element={<SnapBidLanding />} />
      <Route path="/pacchetti-sopralluoghi" element={<PacksPublic />} />
      <Route path="/privati" element={<PrivatiHome />} />
      <Route path="/casa" element={<CasaLanding />} />
      <Route path="/casa/studio" element={<CasaStudio />} />
      <Route path="/casa/progetti" element={<CasaStudio />} />
      <Route path="/casa/accedi" element={<CasaAuth mode="login" />} />
      <Route path="/casa/registrati" element={<CasaAuth mode="signup" />} />
      <Route path="/casa/reimposta-password" element={<CasaReset />} />
      <Route path="/impresa/sopralluogo/:rid" element={<ImpresaContatto />} />
      <Route path="/casa/sopralluogo" element={<CasaSopralluogoPage />} />
      <Route path="/casa/pagamenti-protetti" element={<CasaPagamentiPage />} />
      <Route path="/casa/*" element={<Navigate to="/casa" replace />} />
      <Route path="/privati/richiedi-lavoro" element={<RequestWizard />} />
      <Route path="/privati/come-funziona" element={<InfoPage page="come-funziona" />} />
      <Route path="/privati/pagamento-protetto" element={<InfoPage page="pagamento-protetto" />} />
      <Route path="/privati/protezione" element={<InfoPage page="protezione" />} />
      <Route path="/privati/artigiani-verificati" element={<InfoPage page="artigiani-verificati" />} />
      <Route path="/verified" element={<InfoPage page="verified" />} />
      <Route path="/verified/:publicId" element={<VerifiedPublic />} />
      <Route path="/privati/accesso" element={<CustomerAccess />} />
      <Route path="/cliente/dashboard" element={<CustomerDashboard />} />
      <Route path="/cliente/lavori/:id" element={<CustomerJob />} />
      <Route path="/cliente/pagamenti/:id" element={<CustomerJob tab="pagamenti" />} />
      <Route path="/cliente/documenti" element={<CustomerDocuments />} />
      <Route path="/cliente/assistenza" element={<CustomerSupport />} />
      <Route path="/protezione" element={<Protection />} />
      <Route path="/professionisti" element={<Directory />} />
      <Route path="/ai-secretary" element={<SofiaLanding />} />
      <Route path="/founder" element={<FounderLanding />} />
      <Route path="/attiva" element={<IntentRedirect target="/app/founder?avvia=1" />} />
      <Route path="/attiva-mensile" element={<IntentRedirect target="/app/founder?avvia=1&piano=mensile" />} />
      <Route path="/pagamento-ricevuto" element={<PaymentReceived />} />
      <Route path="/ordine-bonifico/:id" element={<BankOrder />} />
      <Route path="/bonifico" element={<BankStart />} />
      <Route path="/sopralluoghi-confermati" element={<ConfirmedInspections />} />
      <Route path="/attiva-account" element={<ActivateAccount />} />
      <Route path="/vision" element={<VisionLanding />} />
      <Route path="/vision/demo-render" element={<VisionDemoRender />} />
      <Route path="/v/:token" element={<VisionShare />} />
      <Route path="/founder/grazie" element={<FounderThanks />} />
      <Route path="/founder/demo-confermata" element={<FounderConfirmed />} />
      <Route path="/candidatura/confermata" element={<ApplicationConfirmed />} />
      <Route path="/avvisi-sopralluoghi/:token" element={<OppAlertsConsent />} />
      <Route path="/founder/meta" element={<FounderMeta />} />
      <Route path="/academy" element={<AcademyLanding />} />
      <Route path="/demo/:token" element={<DemoManage />} />
      <Route path="/d/:code" element={<DemoShort />} />
      <Route path="/platinum" element={<PlatinumLanding />} />
      <Route path="/esempi/:slug" element={<RossiSite />} />
      <Route path="/preventivo-gratis" element={<FreeEstimate />} />
      <Route path="/r/:code" element={<RefRedirect />} />
      <Route path="/pro/:slug" element={<ProProfile />} />
      <Route path="/s/:slug" element={<PublicSite />} />
      <Route path="/s/:slug/:service" element={<PublicSite />} />
      <Route path="/carica/:token" element={<ClientUpload />} />
      <Route path="/snapbid-video-export" element={<SnapBidVideoExport />} />
      <Route path="/explainer-export" element={<ExplainerExport />} />
      <Route path="/q/:token" element={<PublicQuote />} />
      <Route path="/appointment/:token" element={<AppointmentPublic />} />
      <Route path="/c/:token" element={<PublicContract />} />
      <Route path="/verifica-email" element={<VerifyEmail />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/admin/master" element={<MasterAdmin />} />
      <Route path="/story-export" element={<StoryExport />} />
      <Route path="/admin/master/:section" element={<MasterAdmin />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/app" element={<Protected><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="commesse" element={<Jobs />} />
        <Route path="commesse/:id" element={<JobDetail />} />
        <Route path="scan" element={<ScanWizard />} />
        <Route path="vision" element={<VisionProjects />} />
        <Route path="vision/:id" element={<VisionStudio />} />
        <Route path="preventivi" element={<Quotes />} />
        <Route path="preventivi/:id" element={<QuoteEditor />} />
        <Route path="lavori" element={<WorkOrders />} />
        <Route path="lavori/:id" element={<WorkOrderDetail />} />
        <Route path="clienti" element={<Customers />} />
        <Route path="clienti/:id" element={<CustomerDetail />} />
        <Route path="listino" element={<PriceList />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="abbonamento" element={<Pricing />} />
        <Route path="impostazioni" element={<Settings />} />
        <Route path="altro" element={<More />} />
        <Route path="notifiche" element={<Notifications />} />
        <Route path="snapbid" element={<SnapBid />} />
        <Route path="snapbid/profilo" element={<SnapBidProfile />} />
        <Route path="snapbid/acquistati" element={<Purchased />} />
        <Route path="snapbid/:id" element={<AuctionDetail />} />
        <Route path="acconti-protetti" element={<ProtectedDeposits />} />
        <Route path="saldo-protetto" element={<SaldoProtetto />} />
        <Route path="verified" element={<VerifiedApply />} />
        <Route path="sito" element={<Website />} />
        <Route path="profilo-pubblico" element={<PublicProfileEdit />} />
        <Route path="richieste" element={<Leads />} />
        <Route path="platinum" element={<Platinum />} />
        <Route path="sofia" element={<Sofia />} />
        <Route path="sopralluoghi" element={<Sopralluoghi />} />
        <Route path="crediti" element={<Credits />} />
        <Route path="pacchetti" element={<Packs />} />
        <Route path="founder" element={<FounderArea />} />
        <Route path="salva-bozza" element={<ClaimDraft />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function SalesAttach() {
  const { user } = useAuth();
  useEffect(() => {
    const t = sessionStorage.getItem("sq_sales_lead");
    localStorage.removeItem("sq_sales_lead");
    if (user && t) { api.post("/sales/attach", { lead_token: t }).finally(() => sessionStorage.removeItem("sq_sales_lead")); }
  }, [user]);
  return null;
}

// New page via link opens from the top; anchors (#id) scroll to their section; back/forward keeps browser position.
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const nav = useNavigationType();
  useEffect(() => {
    if (nav === "POP") { return undefined; }
    if (!hash) { window.scrollTo(0, 0); return undefined; }
    const t = setTimeout(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "smooth" }), 120);
    return () => clearTimeout(t);
  }, [pathname, hash, nav]);
  return null;
}

export default function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <AuthProvider>
        <RouteMeta />
        <AppRoutes />
        <RefCapture />
        <SalesAttach />
        <WhatsAppButton />
        <CookieBanner />
        <MetaPixel />
        <AdsConversion />
        <Toaster position="top-center" theme="dark" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}
